import { cn } from '../lib/classnames.js';

/**
 * Identicon — deterministic 5x5 SVG glyph, mirrored across the vertical
 * axis, gold-on-parchment. The single canonical attribution swatch used
 * across Feed, Remix, Leaderboard, and anywhere else the maker is shown.
 *
 * Keyed off the address bytes. Hue stays within the brand's accent band,
 * so the swatch never disappears against a parchment or dark surface.
 */
function bytesFromAddress(addr: string): number[] {
  if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) return [...new TextEncoder().encode(addr)];
  const hex = addr.toLowerCase().replace(/^0x/, '');
  const out: number[] = [];
  for (let i = 0; i < hex.length; i += 2) {
    out.push(parseInt(hex.slice(i, i + 2), 16));
  }
  return out;
}

export function Identicon({
  address,
  size = 32,
  className,
}: {
  address: string;
  size?: number;
  className?: string;
}) {
  const bytes = bytesFromAddress(address);
  const cells: boolean[][] = Array.from({ length: 5 }, () =>
    Array.from({ length: 5 }, () => false),
  );
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 3; col++) {
      const idx = (row * 3 + col) % Math.max(bytes.length, 1);
      const lit = (bytes[idx] ?? 0) % 2 === 0;
      const r = cells[row];
      if (!r) continue;
      r[col] = lit;
      r[4 - col] = lit; // mirror
    }
  }
  // Subtle hue rotation per address — accent band only, never grey or random hsl.
  const hueSeed = (bytes[0] ?? 0) % 3;
  const fill =
    hueSeed === 0
      ? 'rgb(var(--color-accent))'
      : hueSeed === 1
        ? 'rgb(var(--color-accent-deep))'
        : 'rgb(var(--color-accent-soft))';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 5 5"
      aria-hidden="true"
      className={cn('shrink-0 rounded-xs', className)}
      style={{
        background: 'rgb(var(--color-surface-sunken))',
        boxShadow: 'inset 0 0 0 1px rgb(var(--color-hairline) / 0.6)',
      }}
    >
      {cells.map((row, r) =>
        row.map((on, c) =>
          on ? (
            <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill={fill} />
          ) : null,
        ),
      )}
    </svg>
  );
}

/**
 * AddressBadge — Identicon + short address as a single inline element.
 * Used in feed entries, remix attribution, and anywhere a maker is
 * inline-referenced. ENS lookup is a future enhancement.
 */
export function AddressBadge({
  address,
  displayName,
  size = 16,
  className,
}: {
  address: string;
  displayName?: string | null;
  size?: number;
  className?: string;
}) {
  const short = `${address.slice(0, 6)}…${address.slice(-4)}`;
  const label = displayName ?? short;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 text-caption text-ink-subtle',
        className,
      )}
      title={address}
    >
      <Identicon address={address} size={size} />
      <span className="font-display italic">{label}</span>
    </span>
  );
}
